import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useConnectImap } from '@/hooks/useMail'
import type { MailScope } from '@/types/api/mail'
import { getErrorMessage } from '@/utils/error-parser'

/** Connects a mailbox on any mail server that offers IMAP (to read) and SMTP (to send). */
export function ImapConnectDialog({ scope, onClose }: { scope: MailScope; onClose: () => void }) {
  const connect = useConnectImap()
  const [form, setForm] = useState({
    emailAddress: '', username: '', password: '',
    imapHost: '', imapPort: '993', smtpHost: '', smtpPort: '587',
  })
  const [error, setError] = useState<string | null>(null)
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const complete = Object.values(form).every((v) => v.trim() !== '')

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    connect.mutate(
      { scope, ...form, imapPort: Number(form.imapPort), smtpPort: Number(form.smtpPort) },
      {
        onSuccess: (connection) => {
          toast.success(`${connection.emailAddress} connected`)
          onClose()
        },
        onError: (err) => setError(getErrorMessage(err, 'Could not connect the mailbox')),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Connect a mailbox with IMAP</DialogTitle>
            <DialogDescription>
              For a mailbox on your own mail server. Many providers require an app password instead of your normal one.
              The password is stored encrypted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="imap-email">Email address</Label>
              <Input id="imap-email" type="email" autoComplete="off" value={form.emailAddress} onChange={set('emailAddress')} placeholder="support@yourcompany.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="imap-user">Username</Label>
              <Input id="imap-user" autoComplete="off" value={form.username} onChange={set('username')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="imap-pass">Password</Label>
              <Input id="imap-pass" type="password" autoComplete="new-password" value={form.password} onChange={set('password')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="imap-host">IMAP server</Label>
              <Input id="imap-host" value={form.imapHost} onChange={set('imapHost')} placeholder="imap.yourcompany.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="imap-port">IMAP port</Label>
              <Input id="imap-port" inputMode="numeric" value={form.imapPort} onChange={set('imapPort')} />
              <p className="text-[11px] text-neutral-500">993 (SSL) or 143</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-host">SMTP server</Label>
              <Input id="smtp-host" value={form.smtpHost} onChange={set('smtpHost')} placeholder="smtp.yourcompany.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-port">SMTP port</Label>
              <Input id="smtp-port" inputMode="numeric" value={form.smtpPort} onChange={set('smtpPort')} />
              <p className="text-[11px] text-neutral-500">587 (STARTTLS) or 465 (SSL)</p>
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive-600">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!complete || connect.isPending}>
              {connect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
